import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.js';
import { useNavigate } from 'react-router-dom';

const VideoListTest = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [tokenTest, setTokenTest] = useState(null);
  const [testingToken, setTestingToken] = useState(false);
  const { token, user, login } = useAuth();
  const navigate = useNavigate();

  // Check authentication state on mount
  useEffect(() => {
    const checkAuth = async () => {
      setAuthLoading(true);
      
      // Check if we have a token in localStorage
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      console.log('Stored token:', storedToken ? 'Exists' : 'None');
      console.log('Stored user:', storedUser ? 'Exists' : 'None');
      console.log('Auth context token:', token ? 'Exists' : 'None');
      console.log('Auth context user:', user ? 'Exists' : 'None');
      
      if (storedToken && storedUser) {
        console.log('Found stored auth data, attempting to restore...');
        try {
          const userData = JSON.parse(storedUser);
          // The auth context should pick this up automatically
          console.log('Restored user data:', userData);
        } catch (e) {
          console.error('Error parsing stored user data:', e);
        }
      }
      
      setAuthLoading(false);
    };
    
    checkAuth();
  }, []);

  const testTokenPermissions = async () => {
    setTestingToken(true);
    setError(null);
    
    // Try to get token from localStorage if not in context
    const authToken = token || localStorage.getItem('token');
    
    if (!authToken) {
      setError('No authentication token available. Please log in first.');
      setTestingToken(false);
      return;
    }
    
    try {
      const response = await fetch('https://elderfit.onrender.com/api/cloudflare-stream/test-token', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Token test response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token test error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Token test response:', data);
      
      setTokenTest(data);
    } catch (err) {
      console.error('Error testing token:', err);
      setError(err.message);
    } finally {
      setTestingToken(false);
    }
  };

  const fetchVideos = async () => {
    setLoading(true);
    setError(null);
    
    // Try to get token from localStorage if not in context
    const authToken = token || localStorage.getItem('token');
    
    console.log('Fetching videos with token:', authToken ? 'Token exists' : 'No token');
    console.log('User:', user);
    
    if (!authToken) {
      setError('No authentication token available. Please log in first.');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch('https://elderfit.onrender.com/api/cloudflare-stream/list-videos', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Video list response:', data);
      
      if (data.success) {
        setVideos(data.videos || []);
      } else {
        setError(data.error || 'Failed to fetch videos');
      }
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    // Store the current URL to return to after login
    localStorage.setItem('returnUrl', '/video-list-test');
    navigate('/login');
  };

  const handleRefreshAuth = () => {
    // Force refresh the authentication state
    window.location.reload();
  };

  const handleDirectLogin = () => {
    // Try to get stored credentials and auto-login
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      console.log('Attempting direct login with stored credentials...');
      try {
        const userData = JSON.parse(storedUser);
        // Force the auth context to recognize the stored data
        window.location.reload();
      } catch (e) {
        console.error('Error with stored credentials:', e);
        handleLogin();
      }
    } else {
      handleLogin();
    }
  };

  useEffect(() => {
    if (!authLoading && (token || localStorage.getItem('token'))) {
      fetchVideos();
    }
  }, [token, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              Cloudflare Stream Video List
            </h1>
            <div className="text-center">
              <div className="text-lg">Checking authentication...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const authToken = token || localStorage.getItem('token');
  const authUser = user || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Cloudflare Stream Video List
          </h1>
          
          <div className="mb-4 space-y-2">
            <div className="text-sm text-gray-600">
              <strong>User:</strong> {authUser?.email || authUser?.name || 'Not logged in'}
            </div>
            <div className="text-sm text-gray-600">
              <strong>Token:</strong> {authToken ? 'Available' : 'Not available'}
            </div>
            <div className="text-sm text-gray-600">
              <strong>Context Token:</strong> {token ? 'Available' : 'Not available'}
            </div>
            <div className="text-sm text-gray-600">
              <strong>Stored Token:</strong> {localStorage.getItem('token') ? 'Available' : 'Not available'}
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={fetchVideos}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh Video List'}
              </button>
              
              <button
                onClick={testTokenPermissions}
                disabled={testingToken}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {testingToken ? 'Testing...' : 'Test Token Permissions'}
              </button>
              
              {!authToken && (
                <>
                  <button
                    onClick={handleLogin}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    Go to Login
                  </button>
                  
                  <button
                    onClick={handleDirectLogin}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                  >
                    Try Direct Login
                  </button>
                  
                  <button
                    onClick={handleRefreshAuth}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
                  >
                    Refresh Auth State
                  </button>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <strong>Error:</strong> {error}
            </div>
          )}

          {tokenTest && (
            <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
              <h3 className="font-semibold mb-2">Token Permission Test Results:</h3>
              <div className="text-sm space-y-1">
                <div><strong>User Access:</strong> {tokenTest.results?.userAccess ? ' Success' : ' Failed'}</div>
                <div><strong>User Email:</strong> {tokenTest.results?.userData?.email || 'N/A'}</div>
                <div><strong>Accounts Access:</strong> {tokenTest.results?.accountsAccess ? ' Success' : ' Failed'}</div>
                <div><strong>Target Account Access:</strong> {tokenTest.results?.targetAccountAccess ? ' Success' : ' Failed'}</div>
                {tokenTest.results?.accessibleAccounts && (
                  <div>
                    <strong>Accessible Accounts:</strong>
                    <ul className="ml-4 mt-1">
                      {tokenTest.results.accessibleAccounts.map((acc, index) => (
                        <li key={index} className="text-xs">
                          {acc.name} ({acc.id}) - {acc.type}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {tokenTest.results?.targetAccountError && (
                  <div className="text-red-600">
                    <strong>Target Account Error:</strong> {tokenTest.results.targetAccountError}
                  </div>
                )}
              </div>
            </div>
          )}

          {!authToken && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              <strong>Authentication Required:</strong> Please log in to view your Cloudflare Stream videos.
              <br />
              <strong>Note:</strong> If you're already logged in on another tab, try "Try Direct Login" or "Refresh Auth State".
            </div>
          )}

          {videos.length === 0 && !loading && !error && authToken && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              <strong>No videos found</strong> in your Cloudflare Stream account.
            </div>
          )}

          {videos.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Found {videos.length} video(s):
              </h2>
              
              {videos.map((video, index) => (
                <div key={video.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {video.name || 'Untitled Video'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        <strong>ID:</strong> <code className="bg-gray-100 px-2 py-1 rounded">{video.id}</code>
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Status:</strong> {video.status}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        <strong>Duration:</strong> {video.duration ? `${Math.round(video.duration / 60)} min` : 'Unknown'}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Size:</strong> {video.size ? `${Math.round(video.size / 1024 / 1024)} MB` : 'Unknown'}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Created:</strong> {video.created ? new Date(video.created).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(video.id);
                        alert('Video ID copied to clipboard!');
                      }}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                    >
                      Copy Video ID
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 p-4 bg-gray-100 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">Instructions:</h3>
            <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
              <li>Click "Test Token Permissions" to check what accounts your API token can access</li>
              <li>If not logged in, try "Try Direct Login" first, then "Go to Login"</li>
              <li>After logging in, you'll be redirected back to this page</li>
              <li>Click "Copy Video ID" for the video you want to use</li>
              <li>Update the video ID in your fitspiration exercises</li>
              <li>Test the video playback</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoListTest;
